import { IWishlist } from "../models/IWishlist.model";
import { IWishlistResponse } from "../models/IWishlistResponse.model";
import { checkStatus, deleteRequest, get, post, put, toJson } from "../../../gulp/scripts/typescript/helpers/fetch";
import { IErrorResponse } from "../models/IErrorResponse";
import { IWishlistItem } from "../models/IWishlistItem.model";
import { ILineItem } from "../../../basketsrc/src/models/ILineItem.model";
import { IPrice } from "../../../basketsrc/src/models/IPrice.model";
import { IGA4Item } from "../models/IGA4Item";
import AlgoliaTracking from "../../../gulp/scripts/typescript/modules/algolia-tracking";
import Ecommerce from "../../../gulp/scripts/typescript/modules/ecommerce";
import Tracking from "../../../gulp/scripts/typescript/modules/tracking";
import {IViewItemGA4Item} from "../../../gulp/scripts/ui/models/IViewItemGA4Item";

declare var dataLayer: any;

class WishlistService {

	async getWishlist(): Promise<IWishlist> {
		return get('/api/wishlist')
			.then(checkStatus)
			.then(toJson);
	}

	async removeFromWishlist(lineItemGuid: string): Promise<IWishlistResponse> {
		return deleteRequest(`/api/wishlist/lineitem/${lineItemGuid}`)
			.then(checkStatus)
			.then(toJson);
	}

	async updateWishlistItem(lineItemGuid: string, quantity: number): Promise<IWishlistResponse> {

		return put(`/api/wishlist/lineitem/${lineItemGuid}`,
			{
				quantity: quantity
			}
		)
			.then(checkStatus)
			.then(toJson);
	}

	async moveItemToBasket(lineItemGuid: string, quantity: number): Promise<IWishlistResponse> {

		return put(`/api/wishlist/move-to-basket/${lineItemGuid}`,
			{
				quantity: quantity
			}
		)
			.then(checkStatus)
			.then(toJson);
	}

	createSharableUrl = (products: IWishlistItem[], trackingOptions?: { category: string; medium: string; content: string }) => {
		const linkInputField = document.querySelector('.js-wishlist-share-link-input') as HTMLInputElement;
		let searchParams = new URLSearchParams();
		for (var i = 0; i < products.length; i++) {
			searchParams.append("sku", `${products[i].Product.Name}${products[i].Quantity > 1 ? `:${products[i].Quantity}` : ''}`);
		}
		let urlSuffix = `/w?${searchParams}`;

		const applyTracking = (url: string) => {
			if (trackingOptions) {
				try {
					return Tracking.addCampaignTracking(url, trackingOptions.category, trackingOptions.medium, trackingOptions.content);
				} catch {}
			}
			return url;
		};

		fetch("/api/shorturl/wishlist", {
			headers: {
				'Content-Type': 'application/json'
			},
			method: "POST",
			body: JSON.stringify({
				"destinationUrl": urlSuffix
			})
		}).then(response => response.json())
			.then((data) => {
				linkInputField.value = applyTracking(window.location.origin + data);
			})
			.catch((error) => {
				linkInputField.value = applyTracking(window.location.origin + urlSuffix);
			});
	};

	async moveAllItemsToBasket(): Promise<IWishlistResponse | IErrorResponse> {
		return put("/api/wishlist/move-to-basket", { })
			.then(response => {
				if (response.status == 200) {
					return Promise.resolve(response.json());
				} else {
					return Promise.reject(new Error(response.statusText));
				}
			});
	}

	setWishlistSummaryCount = (quantity: number) => {
		const wishlistCountElement = document.querySelector('.js-wishlist-count');
		if (wishlistCountElement) {
			wishlistCountElement.textContent = quantity.toString();
		}
		if (!quantity) {
			const shareWishlist = document.querySelector('.js-wishlist-share');
			if (shareWishlist) {
				shareWishlist.setAttribute('aria-hidden', 'true');
			}
		}
	};

	setBasketSummaryCount = (quantity: number) => {
		Ecommerce.updateBasketCounts(quantity);
	};

	trackAddToWishlist = (response: any) => {

		let trackingData = {
			value: 0,
			items: [] as IGA4Item[]
		};

		for (let i = 0, len = response.AddedProducts.length; i < len; i++) {
			const item = response.AddedProducts[i];

			trackingData.items.push({
				item_id: item.Id,
				item_name: item.DisplayName,
				price: item.Price.Price,
				quantity: item.Quantity
			});

			trackingData.value += item.Price.Price * item.Quantity;
		}

		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: 'add_to_wishlist',
			ecommerce: trackingData
		});

		const eventName = AlgoliaTracking.queryID() && AlgoliaTracking.isSearchPage() ? 'Product Added To Wishlist After Search' : 'Product Added To Wishlist';
		const addedProductsThatAreNotParents = response.AddedProducts.filter((product: any) => !product.HasVariants);
		if (addedProductsThatAreNotParents.length) {
			AlgoliaTracking.convertedObjectIDs(eventName, addedProductsThatAreNotParents.map((product: any) => product.Guid));
		}

	}

	trackRemoveFromWishlist = (product: {
		Id: string;
		Price: IPrice;
		Quantity: number;
		DisplayName: string;
	}) => {

		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: 'remove_from_wishlist',
			ecommerce: {
				value: (product.Price.Price * product.Quantity).toFixed(2),
				items: [{
					item_id: product.Id,
					item_name: product.DisplayName,
					price: product.Price.Price,
					quantity: product.Quantity
				}]
			}
		});
	}

	trackMoveToBasket = (items: IWishlistItem[]) => {

		let trackingData = {
			value: 0,
			items: [] as IViewItemGA4Item[]
		};

		let algoliaProductIds: string[] = [];
		let algoliaProductData: {
			price: number,
			discount?: number,
			quantity: number
		}[] = [];

		for (let i = 0, len = items.length; i < len; i++) {
			const item = items[i];
			item.ViewItemEventInfo.quantity = item.Quantity;
			trackingData.items.push(item.ViewItemEventInfo);

			trackingData.value += item.Product.Price.Price * item.Quantity;

			algoliaProductIds.push(item.Product.Guid);
			algoliaProductData.push({
				price: item.Product.Price.Price,
				quantity: item.Quantity
			});
		}

		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: 'move_to_basket_from_wishlist',
			ecommerce: trackingData
		});

		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: 'add_to_cart',
			ecommerce: trackingData
		});

		AlgoliaTracking.addedToCart(algoliaProductIds, algoliaProductData, trackingData.value);

	}

	trackMoveToWishlistFromBasket = (item: ILineItem) => {
		item.ViewItemEventInfo.quantity = item.Quantity;
		let trackingData = {
			value: item.Price.Price * item.Quantity,
			items: [item.ViewItemEventInfo]
		};

		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: 'move_to_wishlist_from_basket',
			ecommerce: trackingData
		});

		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: 'remove_from_cart',
			ecommerce: trackingData
		});
		AlgoliaTracking.convertedObjectIDs('Product Added To Wishlist From Basket', [item.ProductGuid]);

	}

	trackViewWishlist = (wishlist: IWishlist) => {

		if (!wishlist.Products) {
			return;
		}

		let trackingData = {
			value: 0,
			items: [] as IGA4Item[]
		};

		for (let i = 0, len = wishlist.Products.length; i < len; i++) {
			const item = wishlist.Products[i];

			trackingData.items.push({
				item_id: item.Product.Name,
				item_name: item.Product.DisplayName,
				price: item.Product.Price.Price,
				quantity: item.Quantity
			});

			trackingData.value += item.Product.Price.Price * item.Quantity;
		}

		dataLayer.push({ ecommerce: null });
		dataLayer.push({
			event: 'view_wishlist',
			ecommerce: trackingData
		});
	}

}

export default WishlistService;
